// ─────────────────────────────────────────────────────────
// components/ConnectionStatus.tsx
//
// Phase 6 upgrade:
//   + Shows pending queue count badge when > 0
//   + Shows last-sync timestamp on hover (title attr)
//   + Manual "Sync now" button visible when online + queue > 0
//   + Uses useOffline() so queue size and lastSyncAt refresh
//     automatically via the 8 s poller
//
// Phase 10 upgrade:
//   + Reads syncError from uiStore — turns dot red when dead-letter
//     writes have occurred (persistent amber banner rendered by
//     AppLayout's <SyncErrorBanner> component)
// ─────────────────────────────────────────────────────────
import { hasSupabaseConfig } from '@/lib/env'
import { FLAGS }             from '@/lib/flags'
import { useAuthStore }      from '@/store/authStore'
import { useUIStore }        from '@/store/uiStore'
import { useOffline }        from '@/hooks/useOffline'
import { syncNow }           from '@/services/syncEngine'

export default function ConnectionStatus() {
  const { user }                    = useAuthStore()
  const { isOnline, queueSize, lastSyncAt } = useOffline()
  const { syncError }               = useUIStore()

  // Only show when Supabase DB flag is on
  if (!FLAGS.USE_SUPABASE_DB) return null

  const connected  = isOnline && hasSupabaseConfig
  const hasPending = queueSize > 0
  const hasError   = !!syncError

  const lastSyncLabel = lastSyncAt
    ? `Last sync: ${lastSyncAt.toLocaleTimeString()}`
    : 'Never synced'

  const titleText = hasError
    ? `Sync error — ${syncError}`
    : connected
      ? `Connected to Supabase · ${lastSyncLabel}${hasPending ? ` · ${queueSize} write${queueSize !== 1 ? 's' : ''} pending` : ''}`
      : `Supabase offline — data saved locally · ${lastSyncLabel}`

  // Dot colour: red when dead-letter error, amber when queue pending, grey offline, green ok
  const dotBg = hasError
    ? '#EF4444'
    : connected
      ? (hasPending ? '#F59E0B' : '#22C55E')
      : '#94A3B8'
  const dotGlow = hasError
    ? '0 0 0 2px rgba(239,68,68,0.25)'
    : connected
      ? hasPending
        ? '0 0 0 2px rgba(245,158,11,0.2)'
        : '0 0 0 2px rgba(34,197,94,0.2)'
      : 'none'

  const handleSyncNow = () => {
    if (!user?.companyId) return
    syncNow(user.companyId).catch(() => {})
  }

  return (
    <div
      title={titleText}
      style={{
        display:    'flex',
        alignItems: 'center',
        gap:        6,
        fontSize:   10.5,
        color:      'var(--color-tx3)',
        userSelect: 'none',
        cursor:     'default',
      }}
    >
      {/* Status dot */}
      <span style={{
        width:        7,
        height:       7,
        borderRadius: '50%',
        flexShrink:   0,
        background:   dotBg,
        boxShadow:    dotGlow,
        transition:   'background 0.3s, box-shadow 0.3s',
      }} />

      {/* Pending queue badge */}
      {hasPending && !hasError && (
        <span style={{
          background:   '#F59E0B',
          color:        '#fff',
          borderRadius: 10,
          padding:      '0 5px',
          fontSize:     9.5,
          fontWeight:   600,
          lineHeight:   '16px',
          minWidth:     16,
          textAlign:    'center',
        }}>
          {queueSize}
        </span>
      )}

      {/* Error badge */}
      {hasError && (
        <span style={{
          background:   '#EF4444',
          color:        '#fff',
          borderRadius: 10,
          padding:      '0 5px',
          fontSize:     9.5,
          fontWeight:   600,
          lineHeight:   '16px',
          whiteSpace:   'nowrap',
        }}>
          Sync error
        </span>
      )}

      {/* Manual sync button — visible when online + has pending (no error) */}
      {connected && hasPending && !hasError && (
        <button
          onClick={handleSyncNow}
          title="Sync pending writes now"
          style={{
            background:  'transparent',
            border:      '1px solid var(--color-bd)',
            borderRadius: 4,
            padding:     '1px 6px',
            fontSize:    9.5,
            cursor:      'pointer',
            color:       'var(--color-tx3)',
            lineHeight:  '14px',
          }}
        >
          Sync
        </button>
      )}
    </div>
  )
}
